import {
    CMP_CONSENT_CATEGORIES,
    CMP_CONSENT_PLATFORMS,
    PUBLISH_STATUS,
    validConsentConfigModes,
} from './constants';
import { formatDate, makeResultsFormatter } from '../common/services/helpers';
import { locale, translate } from '../common/helpers/i18n';

import { axisNameForTimeFormat } from '../common/components/Charts/helpers';
import castArray from 'lodash/castArray';
import { formatNumber } from '../common/helpers/Number';
import { formatOrganizations } from '../organizations/format';
import { htmlDecode } from '../common/helpers/String';
import identity from 'lodash/identity';
import { parseDate } from '../common/helpers/Date';

export const assignConsentConfig = ccId => details => ({
    ...details,
    consentConfig: ccId,
});

const interpretPublishStatus = ({ publishStatus, updated, lastPublished, mode }) => {
    if (publishStatus === null) {
        return PUBLISH_STATUS.UNPUBLISHED;
    }

    if (publishStatus === 'complete') {
        if (updated === lastPublished) {
            return PUBLISH_STATUS.READY;
        }

        if (updated > lastPublished) {
            return PUBLISH_STATUS.UPDATED;
        }
        const productionMode = mode === 'production';
        const permissiveMode = mode === 'permissive';
        return productionMode || permissiveMode ? PUBLISH_STATUS.READY : PUBLISH_STATUS.MODE_CHANGE;
    }

    return publishStatus;
};

export const formatConfig = details => {
    const {
        domains,
        name,
        mode,
        publishedRevision,
        configuration: {
            fakeIt,
            additionalLinks = [],
            storagePolicyHref = '',
            iab: originalIab = {},
            ...configuration
        } = {},
        orgIds = [],
        organizations = orgIds,
        ...remain
    } = details;

    // CMP-1607: rename iab.eu to iab.tcf
    const { eu: tcf, ...iabRest } = originalIab;
    const renamedIab = tcf
        ? {
              ...iabRest,
              tcf,
          }
        : originalIab;

    // CMP-3420: configs saved before the GPP/TCF split carry only iabEnabled, which meant both.
    const legacyIabEnabled = !!configuration.iabEnabled;
    const iab = {
        ...renamedIab,
        gpp: { ...renamedIab.gpp, enabled: renamedIab.gpp?.enabled ?? legacyIabEnabled },
        tcf: { ...renamedIab.tcf, enabled: renamedIab.tcf?.enabled ?? legacyIabEnabled },
    };

    // CMP-2967: Migrate text from view cookies to view disclosures
    const { translations } = configuration;
    if (
        translations?.messaging?.viewCookies != null &&
        translations.messaging.viewDisclosures == null
    ) {
        translations.messaging.viewDisclosures = translations.messaging.viewCookies;
    }

    // CMP-2994: migrate palette legacy properties for toggle colors
    const newPalette = { ...configuration.palette };
    const paletteMapMigrations = {
        toggleButtonOffColor: 'toggleOffThumbColor',
        toggleButtonOnColor: 'toggleOnThumbColor',
        toggleOffBackgroundColor: 'toggleOffTrackColor',
        toggleOnBackgroundColor: 'toggleOnTrackColor',
    };

    // Migrate old color keys to new ones
    for (const [oldKey, value] of Object.entries(configuration.palette || {})) {
        if (oldKey in paletteMapMigrations) {
            const newKey = paletteMapMigrations[`${oldKey}`];
            newPalette[`${newKey}`] = value || newPalette[`${newKey}`];
            delete newPalette[`${oldKey}`];
            continue;
        } else {
            newPalette[`${oldKey}`] = value || newPalette[`${oldKey}`];
        }
    }

    // Derive toggle state from jurisdiction array
    const analyticsJurisdictions = Array.isArray(configuration.analyticsOnByDefault)
        ? configuration.analyticsOnByDefault
        : [];
    const analyticsOnByDefault = analyticsJurisdictions.length > 0;
    const analyticsOnByDefaultJurisdictions = analyticsJurisdictions.includes('us')
        ? []
        : analyticsJurisdictions;

    return {
        ...remain,
        name: htmlDecode(name), // React automatically encodes HTML under the hood, so we need to provide a decoded string
        publishedRevision,
        configuration: {
            ...configuration,
            analyticsOnByDefault,
            analyticsOnByDefaultJurisdictions,
            iab,
            storagePolicyHref: decodeURI(storagePolicyHref),
            additionalLinks: additionalLinks.map(([text, href]) => [text, decodeURI(href)]),
            palette: newPalette,
        },
        publishStatus: interpretPublishStatus(details),
        mode:
            fakeIt && mode === 'production'
                ? 'permissive'
                : validConsentConfigModes.includes(mode)
                  ? mode
                  : validConsentConfigModes[0],
        domains: castArray(domains),
        organizations: formatOrganizations(organizations),
    };
};
export const formatConfigResults = makeResultsFormatter(formatConfig);

export const formatScanUrl = data => {
    const now = new Date().getTime();
    const newDate = new Date();

    const {
        customerId,
        configId,
        url,
        created = now,
        updated = now,
        lastScan = null,
        nextScan = null,
        ...remain
    } = data;
    let lastScanDate = formatDate(lastScan);

    let nextScanDate;
    if (!lastScan) {
        nextScanDate = newDate.setMonth(newDate.getMonth() + 1);
    } else {
        nextScanDate = nextScan;
    }

    const id = `${customerId}-${configId}_${url}`;
    return {
        customerId,
        configId,
        url,
        scanningActive: true,
        scanStatus: null,
        lastScanReason: null,
        id,
        nextScan: nextScanDate,
        lastScan: lastScanDate,
        created,
        updated,
        ...remain,
    };
};
export const formatScanUrlResults = makeResultsFormatter(formatScanUrl);

export const formatSitemapURL = data => {
    const now = new Date().getTime();
    const newDate = new Date();
    const {
        customerId,
        configId,
        url,
        created = now,
        updated = now,
        lastScan = null,
        nextScan = null,
        ...remain
    } = data;
    let lastScanDate = formatDate(lastScan);
    const id = `${customerId}-${configId}_${url}`;

    let nextScanDate;
    if (!lastScan) {
        nextScanDate = newDate.setMonth(newDate.getMonth() + 1);
    } else {
        nextScanDate = nextScan;
    }

    return {
        customerId,
        configId,
        url,
        scanningActive: true,
        lastScanStatus: null,
        lastScanReason: null,
        lastScanError: null,
        id,
        nextScan: nextScanDate,
        lastScan: lastScanDate,
        created,
        updated,
        ...remain,
    };
};

export const formatSitemapURLResults = makeResultsFormatter(formatSitemapURL);

/**
 * Determines the rule and ruleType based on the discovery pattern. This is meant to
 * be used as a fallback for curated rules that don't have an explicit `ruleType`
 * property, since older rules may not have that property, but follow specific
 * patterns that we can recognize
 *
 * @param {string} discovery
 * @param {string} [storeType]
 * @return {[string, string|null]} A tuple of the rule string and the determined rule type (or null if it can't be determined)
 */
function parseRuleFromDiscovery(discovery, storeType) {
    storeType = typeof discovery === 'string' ? storeType : discovery.storeType || storeType;
    let rule = typeof discovery === 'string' ? discovery : discovery.pattern ?? discovery.storeKey;
    let ruleType = typeof discovery === 'string' ? null : discovery.matchType ?? null;
    switch (storeType) {
        case 'cookie':
        case 'localStorage': {
            if (ruleType === 'EXACT_MATCH' || (rule.startsWith('^') && rule.endsWith('$'))) {
                rule = rule.replace(/^\^/, '').replace(/\$$/, '');
                return [rule, 'EXACT_MATCH'];
            }
            if (ruleType === 'STARTS_WITH' || (rule.startsWith('^') && rule.endsWith('.*'))) {
                return [rule.replace(/^\^/, '').replace(/\.\*$/, ''), 'STARTS_WITH'];
            }
            if (ruleType === 'ENDS_WITH' || (rule.startsWith('.*') && rule.endsWith('$'))) {
                return [rule.replace(/^\.\*/, '').replace(/\$$/, ''), 'ENDS_WITH'];
            }
            if (ruleType === 'CONTAINS' || (rule.startsWith('.*') && rule.endsWith('.*'))) {
                return [rule.replace(/^\.\*/, '').replace(/\.\*$/, ''), 'CONTAINS'];
            }
            break;
        }
        case 'script':
        case 'iframe': {
            // Other store types have older patterns that we need to convert.
            if (ruleType === 'CONTAINS') {
                // All the old 'CONTAINS' patterns were domain matches
                try {
                    // Remove escaped escape characters since a domain match wouldn't need those
                    const testUrl = rule.replace(/[\\]/g, '');
                    const url = new URL('https://' + testUrl);
                    rule = url.hostname;
                    return [rule, 'DOMAIN'];
                } catch {
                    // If it can't be parsed as a URL, it also can't match a domain rule. Leave the pattern as-is since it may still match as a regex.
                }
            }
            if (ruleType === 'STARTS_WITH') {
                // All the old 'STARTS_WITH' patterns were also domain matches, with a schema
                try {
                    // Remove escaped escape characters since a domain match wouldn't need those
                    const testUrl = rule.replace(/[\\]/g, '');
                    const url = new URL(testUrl); // If it didn't have a schema, it would throw
                    rule = url.hostname;
                    return [rule, 'DOMAIN'];
                } catch {
                    // If it can't be parsed as a URL, it also can't match a domain rule. Leave the pattern as-is since it may still match as a regex.
                }
            }
            break;
        }
    }
    return [rule, ruleType];
}
export const formatTattle = data => {
    const { storeKey, storeType, pattern, classification, ...remain } = data;
    const [parsedRule, parsedRuleType] = parseRuleFromDiscovery(data, storeType);
    return {
        ...remain,
        ...(storeType === 'cookie' && {
            disclosure: !!remain.disclosure,
            confidence: remain.confidence || 'none',
        }),
        storeKey,
        classification: classification || 'UNKNOWN',
        pattern: pattern || storeKey,
        // These suggestions will be used as default values in the Review overlay
        rule: parsedRule,
        ruleType: parsedRuleType,
    };
};
export const formatTattleResults = makeResultsFormatter(formatTattle);

export const formatRuleResults = makeResultsFormatter(identity);

/**
 * Parse consentTime into epoch milliseconds.
 * Handles both epoch seconds (number) and date strings ("2026-03-23 15:00:32.000000 UTC").
 */
const parseConsentTime = consentTime => {
    if (typeof consentTime === 'number') {
        return consentTime * 1000;
    }
    if (typeof consentTime === 'string') {
        return new Date(consentTime).getTime();
    }
    return consentTime;
};

/**
 * Parse consented categories.
 * Handles both arrays and comma-separated strings ("ESSENTIAL, PERSONALIZATION, ANALYTICS").
 */
const parseConsentedCategories = consented => {
    if (Array.isArray(consented)) {
        return consented;
    }
    if (typeof consented === 'string') {
        return consented
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);
    }
    return consented;
};

export const formatConsentData = data => {
    // Support both old field names (osnoCustomerId, osnoConfigId, usrIp, usrLang, usrCountry)
    // and new field names (customerId, configId, userIp, userLang, country)
    const customerId = data.osnoCustomerId || data.customerId;
    const ccId = data.osnoConfigId || data.configId;
    const ip = data.usrIp || data.userIp;
    const lang = data.usrLang || data.userLang;
    const country = data.usrCountry || data.country;
    const region = data.usrRegion || data.region;
    const platform = data.consentingPlatform || 'web';
    const domain = data.consentingDomain;

    return {
        customer: customerId,
        ccId,
        userConsentId: data.userConsentId,
        consentedCategories: parseConsentedCategories(data.consented),
        consentTime: parseConsentTime(data.consentTime),
        ip,
        lang,
        country,
        region,
        platform,
        domain,
    };
};

/**
 * Normalize the consent API response to the standard format expected by makeResultsFormatter.
 * Handles both the old paging format ({ data, paging }) and the new metadata/nextToken format ({ data, metadata }).
 */
const normalizeConsentResponse = response => {
    if (response?.metadata) {
        const total = response.metadata.total ?? response.data?.length ?? 0;
        const limit = response.metadata.limit;
        const nextPage = response.metadata.nextToken || null;
        return {
            results: response.data,
            paging: {
                count: total,
                hasNextPage: !!nextPage,
                limit,
                nextPage,
                offset: 0,
                pageNumber: 1,
                page: 1,
                pageSize: limit,
            },
        };
    }
    return response;
};

export const formatConsentDataResults = response =>
    makeResultsFormatter(formatConsentData)(normalizeConsentResponse(response));

/*
{
    labels: ['1 Aug', '2 Aug', '3 Aug', '4 Aug', '5 Aug', '6 Aug'],
    datasets: [
        {
            label: 'ConsentConfig',
            backgroundColor: palette.primary.main,
            data: [18000, 5000, 19000, 27000, 29000, 19000, 20000],
        },
        {
            label: 'Declines',
            backgroundColor: palette.common.neutral,
            data: [11000, 20000, 12000, 29000, 30000, 25000, 13000],
        },
    ],
}
*/

/**
 * @template {string} C
 * @template {string} R
 * @param {C} divisionOrCountryCode
 * @param {R} [regionCode]
 * @returns {`` extends `${R}` ? SnakeToKebabCase<Lowercase<C>> : Lowercase<`${SnakeToKebabCase<C>}-${R}`>}
 */
export const toDivisionCode = (divisionOrCountryCode, regionCode = undefined) =>
    `${divisionOrCountryCode.split('_').join('-')}${typeof regionCode === 'string' && regionCode ? `-${regionCode}` : ''}`.toLowerCase();

export const formatConsentReports = (/** @type {CmpReportsResponseData} */ data) => {
    const errors = {};
    const {
        classificationByConfig,
        consentsByJurisdiction,
        consentsByPlatform,
        consentsOverTime,
        stats,
        traffic,
    } = Object.entries(data).reduce((acc, [key, { message, response }]) => {
        if (message === 'success') {
            acc[`${key}`] = response;
            errors[`${key}`] = null;
            return acc;
        }
        errors[`${key}`] = message;
        return acc;
    }, {});

    const [minDatePlatform, maxDatePlatform, minPlatformConsents, maxPlatformConsents] = (
        consentsByPlatform || []
    ).reduce(
        (acc, row) => {
            const date = new Date(row.date).getTime();
            return [
                Math.min(acc[0], date),
                Math.max(acc[1], date),
                Math.min(acc[2], row.totalConsents),
                Math.max(acc[3], row.totalConsents),
            ];
        },
        [Number.MAX_SAFE_INTEGER, 0, Number.MAX_SAFE_INTEGER, 0]
    );
    const [minDate, maxDate, minTimeConsents, maxTimeConsents] = (consentsOverTime || []).reduce(
        (acc, row) => {
            const date = new Date(row.date).getTime();
            return [
                Math.min(acc[0], date),
                Math.max(acc[1], date),
                Math.min(acc[2], row.totalConsents),
                Math.max(acc[3], row.totalConsents),
            ];
        },
        [minDatePlatform, maxDatePlatform, Number.MAX_SAFE_INTEGER, 0]
    );

    return {
        errors,
        dateRange: [minDate, maxDate],
        platformConsentRange: [minPlatformConsents, maxPlatformConsents],
        timeConsentRange: [minTimeConsents, maxTimeConsents],
        consentRange: [
            Math.min(minPlatformConsents, minTimeConsents),
            Math.max(maxPlatformConsents, maxTimeConsents),
        ],
        totalRange: [
            Math.min(minPlatformConsents, minTimeConsents),
            Math.max(maxPlatformConsents, maxTimeConsents),
        ],
        stats,
        traffic,
        classificationByConfig,
        consentsByJurisdiction: consentsByJurisdiction?.map(row => ({
            ...row,
            ...Object.entries(row.consents).reduce(
                (acc, [key, value]) => {
                    if (!CMP_CONSENT_CATEGORIES[`${key}`]) {
                        if (process.env.NODE_ENV === 'development') {
                            // eslint-disable-next-line no-console
                            console.warn(`Unknown category: ${key}`);
                        }
                        return acc;
                    }
                    if (['STORAGE'].includes(key)) {
                        return acc;
                    }
                    acc[`${CMP_CONSENT_CATEGORIES[`${key}`]}TotalConsents`] = value;
                    return acc;
                },
                {
                    jurisdiction: toDivisionCode(row.countryCode, row.regionCode),
                    totalConsents: row.totalConsents,
                }
            ),
        })),
        consentsByPlatform: consentsByPlatform?.map(row => ({
            ...row,
            date: new Date(row.date).getTime(),
            ...Object.entries(row.consents).reduce((acc, [key, value]) => {
                if (!CMP_CONSENT_PLATFORMS[`${key}`]) {
                    if (process.env.NODE_ENV === 'development') {
                        // eslint-disable-next-line no-console
                        console.warn(`Unknown platform: ${key}`);
                    }
                    return acc;
                }
                acc[`${CMP_CONSENT_PLATFORMS[`${key}`]}TotalConsents`] = value;
                return acc;
            }, {}),
        })),
        consentsByCategory: Object.entries(
            (consentsOverTime && consentsOverTime.length
                ? consentsOverTime
                : [{ totalConsents: 0 }]
            ).reduce((acc, row) => {
                Object.entries(
                    Object.keys(CMP_CONSENT_CATEGORIES).reduce((acc, key) => {
                        if (['STORAGE'].includes(key)) {
                            return acc;
                        }
                        acc[`${key}`] = (row?.consents && row?.consents[`${key}`]) || 0;
                        return acc;
                    }, {})
                ).forEach(([key, optIn]) => {
                    if (['STORAGE'].includes(key)) {
                        return;
                    }
                    const category = acc[`${key}`] || {
                        optIn: 0,
                        optOut: 0,
                    };
                    category.optIn += optIn;
                    category.optOut -= row.totalConsents - optIn; // Make the value negative so it shows up on the left side of the chart
                    acc[`${key}`] = category;
                });
                return acc;
            }, {})
        ).map(([category, { optIn, optOut }]) => ({
            category,
            optIn,
            optOut,
        })),
        consentsOverTime: consentsOverTime?.map(row => ({
            ...row,
            date: new Date(row.date).getTime(),
            ...Object.entries(row.consents).reduce((acc, [category, optIn]) => {
                acc[`${CMP_CONSENT_CATEGORIES[`${category}`]}OptIn`] = optIn;
                acc[`${CMP_CONSENT_CATEGORIES[`${category}`]}OptOut`] = row.totalConsents - optIn;
                return acc;
            }, {}),
        })),
    };
};

export const formatConfigRevisions = (data, idx) => {
    const { revisionNumber, created, customerId, description, publishedBy } = data;
    let createdDate = formatDate(created);

    return {
        revisionNumber,
        description,
        created: createdDate,
        customerId,
        latestRevision: idx === 0,
        publishedBy,
    };
};

export const formatConfigRevisionsResults = makeResultsFormatter(formatConfigRevisions);

export const formatChartTooltip = format => params => {
    const { axisValueLabel } = params[0];
    const date = new Date(parseDate(axisValueLabel));
    const loc = locale();
    /** @type {Intl.DateTimeFormatOptions} */
    const formatOptions = {
        timeZone: 'UTC',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    };
    const timeFormat = translate(axisNameForTimeFormat(format), { date: '' }).trim();
    return `${timeFormat ? `<div style="font-size:0.75em;line-height:1;">${timeFormat}</div>` : ''}<b>${date.toLocaleString(loc, formatOptions)}</b><hr style="margin:0.25em 0"/>${params
        .map(({ dimensionNames, marker, seriesName, value }) => {
            const count = value[dimensionNames[1]];
            return `${marker} ${translate(seriesName)}: ${formatNumber(count, loc)}`;
        })
        .join('<br>')}`;
};
